import { copy } from '@ohear/shared'
import { Button, Card, Checkbox, Input, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { AccountTabs } from '../../features/account/AccountTabs'
import { AddressFields } from '../../features/account/AddressFields'
import { EsignBlock, signatureMatchesLegalName } from '../../features/account/EsignBlock'
import { SsnInput } from '../../features/account/SsnInput'
import { TaxFormUpload } from '../../features/account/TaxFormUpload'
import { useAccountProfile } from '../../lib/api/account'
import {
  type TaxProfile,
  type TaxProfileUpdate,
  useTaxProfile,
  useUpdateEin,
  useUpdateLoanOutEin,
  useUpdateSsn,
  useUpdateTaxProfile,
} from '../../lib/api/tax'
import { useT } from '../../lib/i18n/use-t'

/**
 * Actor tax profile editor.
 *
 * Sections (each in a `<Card>`):
 *  1. Tax form — choose W-9 / W-8BEN / W-8BEN-E + upload PDF.
 *  2. Tax ID — SSN (mandatory for US filers) + optional EIN for corp filers.
 *  3. Loan-out company — optional LLC / S-Corp the actor invoices through.
 *  4. Payee info — legal name + mailing address (used for paper checks +
 *     1099 mailings).
 *  5. Contact — phone + alternate email + backup-withholding box.
 *
 * Plus an ESIGN consent block at the bottom whose typed-name input gates
 * the master "Save" button.
 */
export const Route = createFileRoute('/account/tax')({
  component: AccountTaxPage,
})

function AccountTaxPage() {
  const t = useT()
  const profile = useAccountProfile()
  const tax = useTaxProfile()
  const update = useUpdateTaxProfile()
  const ssn = useUpdateSsn()
  const ein = useUpdateEin()
  const loanOutEin = useUpdateLoanOutEin()
  const toast = useToast()

  const isActor = profile.data?.primary_role === 'actor'

  // Form state seeded from the loaded tax profile. We don't manage SSN /
  // EIN here — those go through their own dedicated buttons because the
  // server requires a separate ESIGN-style submission for each.
  const initial = useMemo<TaxProfileUpdate>(
    () => emptyForm(tax.data ?? null),
    [tax.data?.last_updated_at],
  )
  const [form, setForm] = useState<TaxProfileUpdate>(initial)
  const [signature, setSignature] = useState('')

  useEffect(() => {
    setForm(initial)
  }, [initial])

  if (!isActor) {
    return (
      <Section active="tax" isActor={false}>
        <Card>
          <p className="m-0 text-[14px]">{t(copy.common.ACCESS.actor_only_section)}</p>
        </Card>
      </Section>
    )
  }

  if (tax.isLoading || profile.isLoading) {
    return (
      <Section active="tax" isActor>
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> {t(copy.tax.MESSAGES.loading)}
        </div>
      </Section>
    )
  }

  const data: TaxProfile | null = tax.data ?? null
  const formType = data?.form_type ?? 'pending'
  const usingW8 = form.form_type === 'w8ben' || form.form_type === 'w8ben_e'
  const matchesLegal = signatureMatchesLegalName(signature, form.payee_legal_name ?? null)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!matchesLegal) {
      toast.show({ severity: 'error', title: t(copy.tax.MESSAGES.signRequired) })
      return
    }
    update.mutate(
      { ...form, signature_data: signature },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', title: t(copy.tax.MESSAGES.saved) })
        },
        onError: (err) => {
          toast.show({
            severity: 'error',
            title: t(copy.tax.MESSAGES.saveFailed),
            description: err instanceof Error ? err.message : undefined,
          })
        },
      },
    )
  }

  return (
    <Section active="tax" isActor>
      <form onSubmit={submit} className="flex flex-col gap-5 max-w-3xl">
        {/* ── 1. Tax form ─────────────────────────────────────── */}
        <Card title={t(copy.tax.SECTION_HEADINGS.taxForm)}>
          <TaxFormUpload
            formTypeOnFile={formType}
            formUploadedAt={data?.form_uploaded_at ?? null}
            formSha256={data?.form_sha256 ?? null}
            copy={{
              chooseTypeLabel: t(copy.tax.LABELS.chooseFormType),
              typeOptions: [
                { value: 'w9', label: t(copy.tax.FORM_TYPE_OPTIONS.w9) },
                { value: 'w8ben', label: t(copy.tax.FORM_TYPE_OPTIONS.w8ben) },
                { value: 'w8ben_e', label: t(copy.tax.FORM_TYPE_OPTIONS.w8ben_e) },
              ],
              uploadButton: t(copy.tax.LABELS.uploadForm),
              replaceButton: t(copy.tax.LABELS.replaceForm),
              viewButton: t(copy.tax.LABELS.viewForm),
              chooseFile: t(copy.tax.LABELS.uploadForm),
              pdfHint: t(copy.tax.LABELS.pdfHint),
              onFileSince: (when) => `${t(copy.tax.LABELS.formOnFileSince)} ${when}`,
              sha256Label: t(copy.tax.LABELS.sha256Label),
            }}
          />
        </Card>

        {/* ── 2. Tax ID ───────────────────────────────────────── */}
        <Card title={t(copy.tax.SECTION_HEADINGS.taxId)}>
          <div className="flex flex-col gap-4">
            <SsnInput
              last4={data?.ssn_last4 ?? null}
              label={t(copy.tax.LABELS.ssnLabel)}
              hint={t(copy.tax.LABELS.ssnHint)}
              submitLabel={t(copy.tax.LABELS.saveButton)}
              updateLabel={t(copy.tax.LABELS.updateButton)}
              cancelLabel={t(copy.tax.LABELS.cancelButton)}
              busy={ssn.isPending}
              onSubmit={async (digits) => {
                if (!form.payee_legal_name) {
                  throw new Error(t(copy.tax.ESIGN.legalNameMissing))
                }
                await ssn.mutateAsync({
                  ssn: digits,
                  signature: form.payee_legal_name ?? '',
                })
              }}
            />

            <EinField
              last4={data?.ein_last4 ?? null}
              label={t(copy.tax.LABELS.einLabel)}
              hint={t(copy.tax.LABELS.einHint)}
              submitLabel={t(copy.tax.LABELS.saveButton)}
              updateLabel={t(copy.tax.LABELS.updateButton)}
              cancelLabel={t(copy.tax.LABELS.cancelButton)}
              busy={ein.isPending}
              onSubmit={async (d) => {
                await ein.mutateAsync(d)
              }}
            />
          </div>
        </Card>

        {/* ── 3. Loan-out company ─────────────────────────────── */}
        <Card title={t(copy.tax.SECTION_HEADINGS.loanOut)}>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">{t(copy.tax.LABELS.loanOutName)}</span>
              <Input
                value={form.loan_out_name ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, loan_out_name: e.target.value || null }))}
              />
            </label>

            <EinField
              last4={data?.loan_out_ein_last4 ?? null}
              label={t(copy.tax.LABELS.loanOutEin)}
              submitLabel={t(copy.tax.LABELS.saveButton)}
              updateLabel={t(copy.tax.LABELS.updateButton)}
              cancelLabel={t(copy.tax.LABELS.cancelButton)}
              busy={loanOutEin.isPending}
              onSubmit={async (d) => {
                await loanOutEin.mutateAsync(d)
              }}
            />

            <AddressFields
              idPrefix="loan_out"
              value={{
                line1: form.loan_out_address_line1 ?? null,
                line2: form.loan_out_address_line2 ?? null,
                city: form.loan_out_city ?? null,
                state: form.loan_out_state ?? null,
                postal: form.loan_out_postal ?? null,
                country: form.loan_out_country ?? null,
              }}
              onChange={(next) =>
                setForm((f) => ({
                  ...f,
                  loan_out_address_line1: next.line1,
                  loan_out_address_line2: next.line2,
                  loan_out_city: next.city,
                  loan_out_state: next.state,
                  loan_out_postal: next.postal,
                  loan_out_country: next.country,
                }))
              }
              labels={{
                line1: t(copy.tax.LABELS.addressLine1),
                line2: t(copy.tax.LABELS.addressLine2),
                city: t(copy.tax.LABELS.city),
                stateRegion: t(copy.tax.LABELS.stateRegion),
                postal: t(copy.tax.LABELS.postal),
                country: t(copy.tax.LABELS.country),
              }}
            />
          </div>
        </Card>

        {/* ── 4. Payee info ───────────────────────────────────── */}
        <Card title={t(copy.tax.SECTION_HEADINGS.payee)}>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">{t(copy.tax.LABELS.payeeLegalName)}</span>
              <Input
                value={form.payee_legal_name ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, payee_legal_name: e.target.value || null }))
                }
                autoComplete="name"
              />
              <span className="text-[12px] text-[var(--text-2)]">
                {t(copy.tax.LABELS.payeeLegalNameHint)}
              </span>
            </label>

            <AddressFields
              idPrefix="payee"
              value={{
                line1: form.payee_address_line1 ?? null,
                line2: form.payee_address_line2 ?? null,
                city: form.payee_city ?? null,
                state: form.payee_state ?? null,
                postal: form.payee_postal ?? null,
                country: form.payee_country ?? null,
              }}
              onChange={(next) =>
                setForm((f) => ({
                  ...f,
                  payee_address_line1: next.line1,
                  payee_address_line2: next.line2,
                  payee_city: next.city,
                  payee_state: next.state,
                  payee_postal: next.postal,
                  payee_country: next.country,
                }))
              }
              labels={{
                line1: t(copy.tax.LABELS.addressLine1),
                line2: t(copy.tax.LABELS.addressLine2),
                city: t(copy.tax.LABELS.city),
                stateRegion: t(copy.tax.LABELS.stateRegion),
                postal: t(copy.tax.LABELS.postal),
                country: t(copy.tax.LABELS.country),
              }}
            />
          </div>
        </Card>

        {/* ── 5. Contact + flags ─────────────────────────────── */}
        <Card title={t(copy.tax.SECTION_HEADINGS.contact)}>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">{t(copy.tax.LABELS.phone)}</span>
              <Input
                type="tel"
                value={form.phone ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value || null }))}
                autoComplete="tel"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">{t(copy.tax.LABELS.alternateEmail)}</span>
              <Input
                type="email"
                value={form.alternate_email ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, alternate_email: e.target.value || null }))
                }
                autoComplete="email"
              />
            </label>

            {/* W-9-only flag. */}
            {form.form_type === 'w9' || form.form_type === undefined ? (
              <label className="flex items-center gap-2 text-[13px]">
                <Checkbox
                  checked={form.backup_withholding ?? false}
                  onChange={(e) => setForm((f) => ({ ...f, backup_withholding: e.target.checked }))}
                />
                <span>{t(copy.tax.LABELS.backupWithholding)}</span>
              </label>
            ) : null}

            {/* W-8-only fields. */}
            {usingW8 ? (
              <>
                <label className="flex flex-col gap-1 text-[13px]">
                  <span className="font-medium">{t(copy.tax.LABELS.foreignCountry)}</span>
                  <Input
                    value={form.foreign_country_of_residence ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        foreign_country_of_residence: e.target.value || null,
                      }))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1 text-[13px]">
                  <span className="font-medium">{t(copy.tax.LABELS.foreignTreaty)}</span>
                  <Input
                    value={form.foreign_treaty_claim ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        foreign_treaty_claim: e.target.value || null,
                      }))
                    }
                  />
                </label>
              </>
            ) : null}
          </div>
        </Card>

        {/* ── ESIGN consent ──────────────────────────────────── */}
        <Card>
          <EsignBlock
            payeeLegalName={form.payee_legal_name ?? null}
            signatureValue={signature}
            onSignatureChange={setSignature}
            disabled={update.isPending}
            copy={{
              perjuryStatementHeading: usingW8
                ? t(copy.tax.ESIGN.perjuryHeadingW8)
                : t(copy.tax.ESIGN.perjuryHeadingW9),
              perjuryStatementBody: usingW8
                ? t(copy.tax.ESIGN.perjuryBodyW8)
                : t(copy.tax.ESIGN.perjuryBodyW9),
              typeLegalNamePrompt: t(copy.tax.ESIGN.typeLegalNamePrompt),
              typeLegalNameHint: t(copy.tax.ESIGN.typeLegalNameHint),
              namePlaceholder: t(copy.tax.ESIGN.namePlaceholder),
              legalNameMissing: t(copy.tax.ESIGN.legalNameMissing),
            }}
          />
          <div className="mt-4 flex items-center justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={update.isPending}
              disabled={!matchesLegal || update.isPending}
            >
              {t(copy.tax.LABELS.saveButton)}
            </Button>
          </div>
        </Card>
      </form>
    </Section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function emptyForm(profile: TaxProfile | null): TaxProfileUpdate {
  return {
    form_type: profile?.form_type === 'pending' ? undefined : profile?.form_type,
    loan_out_name: profile?.loan_out_name ?? null,
    loan_out_address_line1: profile?.loan_out_address_line1 ?? null,
    loan_out_address_line2: profile?.loan_out_address_line2 ?? null,
    loan_out_city: profile?.loan_out_city ?? null,
    loan_out_state: profile?.loan_out_state ?? null,
    loan_out_postal: profile?.loan_out_postal ?? null,
    loan_out_country: profile?.loan_out_country ?? 'US',
    payee_legal_name: profile?.payee_legal_name ?? null,
    payee_address_line1: profile?.payee_address_line1 ?? null,
    payee_address_line2: profile?.payee_address_line2 ?? null,
    payee_city: profile?.payee_city ?? null,
    payee_state: profile?.payee_state ?? null,
    payee_postal: profile?.payee_postal ?? null,
    payee_country: profile?.payee_country ?? 'US',
    phone: profile?.phone ?? null,
    alternate_email: profile?.alternate_email ?? null,
    backup_withholding: profile?.backup_withholding ?? false,
    foreign_country_of_residence: profile?.foreign_country_of_residence ?? null,
    foreign_treaty_claim: profile?.foreign_treaty_claim ?? null,
  }
}

function Section({
  children,
  isActor,
  active,
}: {
  children: React.ReactNode
  isActor: boolean
  active: 'tax'
}) {
  return (
    <section className="flex flex-col gap-4">
      <AccountTabs active={active} isActor={isActor}>
        {children}
      </AccountTabs>
    </section>
  )
}

/**
 * Inline EIN editor — same display/edit pattern as `SsnInput` but without
 * the typed-signature requirement (EIN is a corp identifier, not a personal
 * tax ID, so the perjury statement at the bottom of the page covers it).
 */
function EinField({
  last4,
  label,
  hint,
  submitLabel,
  updateLabel,
  cancelLabel,
  onSubmit,
  busy = false,
}: {
  last4: string | null
  label: string
  hint?: string
  submitLabel: string
  updateLabel: string
  cancelLabel: string
  onSubmit: (digits: string) => Promise<void> | void
  busy?: boolean
}) {
  const [mode, setMode] = useState<'display' | 'edit'>(last4 ? 'display' : 'edit')
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (mode === 'display' && last4) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-[13px] font-medium">{label}</span>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[14px]">{`●●-●●●${last4}`}</span>
          <Button variant="ghost" onClick={() => setMode('edit')}>
            {updateLabel}
          </Button>
        </div>
      </div>
    )
  }

  const onlyDigits = draft.replace(/\D/g, '')
  const valid = onlyDigits.length === 9

  return (
    <div className="flex flex-col gap-1">
      <label className="flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{label}</span>
        <Input
          value={draft}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, '').slice(0, 9)
            setDraft(d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d)
            setError(null)
          }}
          inputMode="numeric"
          autoComplete="off"
          placeholder="XX-XXXXXXX"
          maxLength={10}
          disabled={busy}
        />
        {hint ? <span className="text-[12px] text-[var(--text-2)]">{hint}</span> : null}
        {error ? <span className="text-[12px] text-[var(--text-2)]">{error}</span> : null}
      </label>
      <div className="flex items-center gap-2 mt-1">
        <Button
          variant="primary"
          loading={busy}
          disabled={!valid || busy}
          onClick={async () => {
            try {
              await onSubmit(onlyDigits)
              setDraft('')
              if (last4) setMode('display')
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Could not save')
            }
          }}
        >
          {submitLabel}
        </Button>
        {last4 ? (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setDraft('')
              setError(null)
              setMode('display')
            }}
          >
            {cancelLabel}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
